import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const TeamPage = () => {
    const [selectedMember, setSelectedMember] = useState(null);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const leadership = [
        {
            id: 1,
            name: "Dr. Amara Okafor",
            role: "Chief Executive Officer & Co-Founder",
            image: "AO",
            bio: "Pioneering physicist who discovered the principles of electromagnetic field computation. Former research lead at CERN, with groundbreaking work in quantum field theory applications.",
            expertise: ["Quantum Physics", "Field Theory", "Distributed Systems"],
            publications: 47,
            patents: 12,
            linkedin: "#",
            twitter: "#"
        },
        {
            id: 2,
            name: "Prof. Chen Wei",
            role: "Chief Technology Officer & Co-Founder",
            image: "CW",
            bio: "Architect of the EAPU system and LightFold protocol. Previously led advanced computing research at MIT's Quantum Engineering Center.",
            expertise: ["Computer Architecture", "Quantum Computing", "System Design"],
            publications: 38,
            patents: 15,
            linkedin: "#",
            twitter: "#"
        },
        {
            id: 3,
            name: "Dr. Sarah Johnson",
            role: "Chief Scientific Officer",
            image: "SJ",
            bio: "Leading expert in continuous computation and electromagnetic interference patterns. Former principal researcher at Stanford AI Lab.",
            expertise: ["Continuous Computing", "AI/ML", "Mathematical Physics"],
            publications: 52,
            patents: 8,
            linkedin: "#",
            twitter: "#"
        },
        {
            id: 4,
            name: "Kwame Asante",
            role: "VP of African Operations",
            image: "KA",
            bio: "Champion of African mathematical heritage in modern computing. Established partnerships with leading African universities and research centers.",
            expertise: ["Strategic Partnerships", "Research Collaboration", "Innovation Policy"],
            publications: 23,
            patents: 5,
            linkedin: "#",
            twitter: "#"
        }
    ];

    const teamMembers = [
        {
            id: 5,
            name: "Dr. Elena Volkov",
            role: "Principal Research Scientist",
            department: "research",
            image: "EV",
            expertise: ["Electromagnetic Theory", "Wave Propagation"]
        },
        {
            id: 6,
            name: "Dr. Fatima Al-Rashid",
            role: "Lead Hardware Engineer",
            department: "engineering",
            image: "FA",
            expertise: ["EAPU Design", "Field Modulators"]
        },
        {
            id: 7,
            name: "Prof. David Kim",
            role: "Director of Software Engineering",
            department: "engineering",
            image: "DK",
            expertise: ["SDK Development", "API Design"]
        },
        {
            id: 8,
            name: "Dr. Maria Santos",
            role: "Quantum Systems Architect",
            department: "research",
            image: "MS",
            expertise: ["Quantum Coherence", "Noise Optimization"]
        },
        {
            id: 9,
            name: "Dr. Yuki Tanaka",
            role: "Senior Research Scientist",
            department: "research",
            image: "YT",
            expertise: ["Field Computation", "Algorithm Design"]
        },
        {
            id: 10,
            name: "Robert Brown",
            role: "VP of Business Development",
            department: "business",
            image: "RB",
            expertise: ["Strategic Partnerships", "Market Analysis"]
        },
        {
            id: 11,
            name: "Dr. Aisha Patel",
            role: "Director of AI Research",
            department: "research",
            image: "AP",
            expertise: ["Continuous AI", "Neural Field Theory"]
        },
        {
            id: 12,
            name: "James Mitchell",
            role: "Chief Operating Officer",
            department: "business",
            image: "JM",
            expertise: ["Operations", "Scaling Strategy"]
        }
    ];

    const advisors = [
        {
            name: "Prof. Michael Chen",
            role: "Quantum Physics Advisor",
            affiliation: "MIT Quantum Engineering Center",
            image: "MC"
        },
        {
            name: "Dr. Wanjiru Kamau",
            role: "African Innovation Advisor",
            affiliation: "University of Nairobi",
            image: "WK"
        },
        {
            name: "Dr. François Dubois",
            role: "Computational Physics Advisor",
            affiliation: "CERN",
            image: "FD"
        },
        {
            name: "Prof. Lisa Wang",
            role: "AI Strategy Advisor",
            affiliation: "Stanford AI Lab",
            image: "LW"
        }
    ];

    const departments = [
        { value: 'all', label: 'All Teams' },
        { value: 'research', label: 'Research' },
        { value: 'engineering', label: 'Engineering' },
        { value: 'business', label: 'Business' }
    ];

    const filteredMembers = filter === 'all'
        ? teamMembers
        : teamMembers.filter(member => member.department === filter);

    return (
    <>
            <style>{`
        .team-hero {
          padding: 10rem 2rem 4rem;
          background: linear-gradient(135deg, var(--white), var(--gray-50));
          position: relative;
          overflow: hidden;
        }

        .team-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, var(--blue-50) 0%, transparent 50%);
          opacity: 0.5;
        }

        .team-hero-content {
          max-width: 1280px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
          text-align: center;
        }

        .team-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 50px;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--gray-700);
          margin-bottom: 2rem;
          box-shadow: var(--shadow-sm);
        }

        .team-hero-title {
          font-size: clamp(3rem, 6vw, 4.5rem);
          font-weight: 800;
          line-height: 1.1;
          color: var(--black);
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
        }

        .team-hero-description {
          font-size: 1.375rem;
          color: var(--gray-600);
          line-height: 1.6;
          max-width: 800px;
          margin: 0 auto 3rem;
        }

        .team-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          max-width: 800px;
          margin: 4rem auto 0;
        }

        .team-stat {
          background: var(--white);
          padding: 2rem;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-md);
          text-align: center;
        }

        .team-stat-number {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--blue-600);
          margin-bottom: 0.5rem;
        }

        .team-stat-label {
          font-size: 1rem;
          color: var(--gray-600);
        }

        /* Leadership Section */
        .leadership-section {
          padding: 6rem 2rem;
          background: var(--white);
        }

        .leadership-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 1rem;
        }

        .section-description {
          font-size: 1.125rem;
          color: var(--gray-600);
          max-width: 600px;
          margin: 0 auto;
        }

        .leadership-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .leader-card {
          background: var(--gray-50);
          border-radius: var(--radius-xl);
          padding: 2rem;
          text-align: center;
          transition: all var(--transition-base);
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .leader-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--blue-50), var(--purple-50));
          opacity: 0;
          transition: opacity var(--transition-base);
        }

        .leader-card:hover::before {
          opacity: 1;
        }

        .leader-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-lg);
        }

        .leader-avatar {
          width: 120px;
          height: 120px;
          background: var(--gradient-primary);
          border-radius: 50%;
          margin: 0 auto 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--white);
          position: relative;
          z-index: 1;
        }

        .leader-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
          position: relative;
          z-index: 1;
        }

        .leader-role {
          font-size: 0.95rem;
          color: var(--blue-600);
          font-weight: 600;
          margin-bottom: 1rem;
          position: relative;
          z-index: 1;
        }

        .leader-stats {
          display: flex;
          justify-content: center;
          gap: 2rem;
          margin-top: 1.5rem;
          position: relative;
          z-index: 1;
        }

        .leader-stat {
          text-align: center;
        }

        .leader-stat-number {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
        }

        .leader-stat-label {
          font-size: 0.75rem;
          color: var(--gray-500);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* Team Section */
        .team-section {
          padding: 6rem 2rem;
          background: var(--gray-50);
        }

        .team-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .team-filters {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 3rem;
        }

        .filter-btn {
          padding: 0.75rem 1.5rem;
          background: var(--white);
          border: 1px solid var(--gray-300);
          border-radius: var(--radius);
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--gray-700);
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .filter-btn:hover {
          border-color: var(--gray-400);
          transform: translateY(-1px);
        }

        .filter-btn.active {
          background: var(--blue-600);
          color: var(--white);
          border-color: var(--blue-600);
        }

        .team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .member-card {
          background: var(--white);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          text-align: center;
          transition: all var(--transition-base);
          border: 1px solid var(--gray-200);
        }

        .member-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
          border-color: var(--blue-200);
        }

        .member-avatar {
          width: 80px;
          height: 80px;
          background: var(--gray-300);
          border-radius: 50%;
          margin: 0 auto 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--white);
        }

        .member-name {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.25rem;
        }

        .member-role {
          font-size: 0.875rem;
          color: var(--gray-600);
          margin-bottom: 1rem;
        }

        .member-expertise {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;
        }

        .expertise-tag {
          padding: 0.25rem 0.75rem;
          background: var(--gray-100);
          border-radius: 50px;
          font-size: 0.75rem;
          color: var(--gray-700);
        }

        /* Advisors Section */
        .advisors-section {
          padding: 6rem 2rem;
          background: var(--white);
        }

        .advisors-container {
          max-width: 1280px;
          margin: 0 auto;
        }

        .advisors-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .advisor-card {
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          padding: 2rem;
          text-align: center;
          border: 1px solid var(--gray-200);
          transition: all var(--transition-base);
        }

        .advisor-card:hover {
          box-shadow: var(--shadow-md);
          transform: translateY(-2px);
        }

        .advisor-avatar {
          width: 100px;
          height: 100px;
          background: var(--gray-400);
          border-radius: 50%;
          margin: 0 auto 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: 700;
          color: var(--white);
        }

        .advisor-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .advisor-role {
          font-size: 0.95rem;
          color: var(--blue-600);
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .advisor-affiliation {
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        /* Member Modal */
        .member-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: var(--z-modal);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          opacity: 0;
          visibility: hidden;
          transition: all var(--transition-base);
        }

        .member-modal.active {
          opacity: 1;
          visibility: visible;
        }

        .modal-content {
          background: var(--white);
          border-radius: var(--radius-xl);
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          transform: scale(0.9);
          transition: transform var(--transition-base);
        }

        .member-modal.active .modal-content {
          transform: scale(1);
        }

        .modal-close {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          width: 40px;
          height: 40px;
          background: var(--gray-100);
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .modal-close:hover {
          background: var(--gray-200);
          transform: rotate(90deg);
        }

        .modal-header {
          padding: 3rem;
          text-align: center;
          border-bottom: 1px solid var(--gray-200);
        }

        .modal-avatar {
          width: 120px;
          height: 120px;
          background: var(--gradient-primary);
          border-radius: 50%;
          margin: 0 auto 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          font-weight: 800;
          color: var(--white);
        }

        .modal-name {
          font-size: 1.75rem;
          font-weight: 800;
          color: var(--gray-900);
          margin-bottom: 0.5rem;
        }

        .modal-role {
          font-size: 1.125rem;
          color: var(--blue-600);
          font-weight: 600;
        }

        .modal-body {
          padding: 3rem;
        }

        .modal-bio {
          font-size: 1.125rem;
          color: var(--gray-700);
          line-height: 1.7;
          margin-bottom: 2rem;
        }

        .modal-section {
          margin-bottom: 2rem;
        }

        .modal-section-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .modal-expertise {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .modal-expertise-tag {
          padding: 0.5rem 1rem;
          background: var(--blue-50);
          color: var(--blue-700);
          border-radius: var(--radius);
          font-size: 0.875rem;
          font-weight: 600;
        }

        .modal-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .modal-stat {
          background: var(--gray-50);
          padding: 1.5rem;
          border-radius: var(--radius);
          text-align: center;
        }

        .modal-stat-number {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--blue-600);
          margin-bottom: 0.25rem;
        }

        .modal-stat-label {
          font-size: 0.875rem;
          color: var(--gray-600);
        }

        .modal-social {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-top: 2rem;
        }

        .social-link {
          width: 44px;
          height: 44px;
          background: var(--gray-100);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--gray-600);
          text-decoration: none;
          transition: all var(--transition-fast);
        }

        .social-link:hover {
          background: var(--blue-600);
          color: var(--white);
          transform: translateY(-2px);
        }

        /* Join CTA Section */
        .join-section {
          padding: 6rem 2rem;
          background: linear-gradient(135deg, var(--blue-600), var(--purple-600));
          color: var(--white);
          text-align: center;
        }

        .join-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .join-title {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 1rem;
        }

        .join-description {
          font-size: 1.25rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }

        .join-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          background: var(--white);
          color: var(--blue-600);
          border-radius: var(--radius);
          text-decoration: none;
          font-weight: 700;
          font-size: 1.125rem;
          transition: all var(--transition-base);
        }

        .join-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
        }

        @media (max-width: 768px) {
          .team-hero {
            padding: 8rem 1rem 3rem;
          }

          .team-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .leadership-grid {
            grid-template-columns: 1fr;
          }

          .team-filters {
            flex-wrap: wrap;
          }

          .filter-btn {
            font-size: 0.875rem;
            padding: 0.5rem 1rem;
          }

          .modal-content {
            margin: 1rem;
          }

          .modal-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="team-hero">
        <div className="team-hero-content">
          <div className="team-hero-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            World-Class Team
          </div>
          <h1 className="team-hero-title">
            Meet the Minds Behind the Revolution
          </h1>
          <p className="team-hero-description">
            A diverse collective of physicists, engineers, mathematicians, and visionaries united by a singular mission: to transcend the limits of computation.
          </p>

          <div className="team-stats">
            <div className="team-stat">
              <div className="team-stat-number">47</div>
              <div className="team-stat-label">Team Members</div>
            </div>
            <div className="team-stat">
              <div className="team-stat-number">12</div>
              <div className="team-stat-label">Countries</div>
            </div>
            <div className="team-stat">
              <div className="team-stat-number">23</div>
              <div className="team-stat-label">PhDs</div>
            </div>
            <div className="team-stat">
              <div className="team-stat-number">156</div>
              <div className="team-stat-label">Combined Patents</div>
            </div>
          </div>
        </div>
      </section>

      <section className="leadership-section">
        <div className="leadership-container">
          <div className="section-header">
            <h2 className="section-title">Leadership Team</h2>
            <p className="section-description">
              Visionary leaders with decades of experience at the intersection of physics and computation
            </p>
          </div>

          <div className="leadership-grid">
            {leadership.map(leader => (
              <div 
                key={leader.id} 
                className="leader-card"
                onClick={() => setSelectedMember(leader)}
              >
                <div className="leader-avatar">{leader.image}</div>
                <h3 className="leader-name">{leader.name}</h3>
                <p className="leader-role">{leader.role}</p>
                <div className="leader-stats">
                  <div className="leader-stat">
                    <div className="leader-stat-number">{leader.publications}</div>
                    <div className="leader-stat-label">Papers</div>
                  </div>
                  <div className="leader-stat">
                    <div className="leader-stat-number">{leader.patents}</div>
                    <div className="leader-stat-label">Patents</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="team-section">
        <div className="team-container">
          <div className="section-header">
            <h2 className="section-title">Our Team</h2>
            <p className="section-description">
              Brilliant minds from diverse backgrounds working together to reshape computation
            </p>
          </div>

          <div className="team-filters">
            {departments.map(dept => (
              <button
                key={dept.value}
                className={`filter-btn ${filter === dept.value ? 'active' : ''}`}
                onClick={() => setFilter(dept.value)}
              >
                {dept.label}
              </button>
            ))}
          </div>

          <div className="team-grid">
            {filteredMembers.map(member => (
              <div key={member.id} className="member-card">
                <div className="member-avatar">{member.image}</div>
                <h3 className="member-name">{member.name}</h3>
                <p className="member-role">{member.role}</p>
                <div className="member-expertise">
                  {member.expertise.map((skill, index) => (
                    <span key={index} className="expertise-tag">{skill}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="advisors-section">
        <div className="advisors-container">
          <div className="section-header">
            <h2 className="section-title">Advisory Board</h2>
            <p className="section-description">
              Guidance from leading experts in quantum physics, AI, and computational theory
            </p>
          </div>

          <div className="advisors-grid">
            {advisors.map((advisor, index) => (
              <div key={index} className="advisor-card">
                <div className="advisor-avatar">{advisor.image}</div>
                <h3 className="advisor-name">{advisor.name}</h3>
                <p className="advisor-role">{advisor.role}</p>
                <p className="advisor-affiliation">{advisor.affiliation}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="join-section">
        <div className="join-container">
          <h2 className="join-title">Join Our Mission</h2>
          <p className="join-description">
            We're always looking for exceptional talent to help us push the boundaries of what's possible
          </p>
          <Link to="/careers" className="join-cta">
            View Open Positions
            <span>→</span>
          </Link>
        </div>
      </section>

      {/* Member Modal */}
      <div className={`member-modal ${selectedMember ? 'active' : ''}`} onClick={() => setSelectedMember(null)}>
        {selectedMember && (
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedMember(null)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="modal-header">
              <div className="modal-avatar">{selectedMember.image}</div>
              <h2 className="modal-name">{selectedMember.name}</h2>
              <p className="modal-role">{selectedMember.role}</p>
            </div>

            <div className="modal-body">
              <p className="modal-bio">{selectedMember.bio}</p>

              <div className="modal-section">
                <h3 className="modal-section-title">Areas of Expertise</h3>
                <div className="modal-expertise">
                  {selectedMember.expertise.map((skill, index) => (
                    <span key={index} className="modal-expertise-tag">{skill}</span>
                  ))}
                </div>
              </div>

              <div className="modal-section">
                <h3 className="modal-section-title">Academic Contributions</h3>
                <div className="modal-stats">
                  <div className="modal-stat">
                    <div className="modal-stat-number">{selectedMember.publications}</div>
                    <div className="modal-stat-label">Publications</div>
                  </div>
                  <div className="modal-stat">
                    <div className="modal-stat-number">{selectedMember.patents}</div>
                    <div className="modal-stat-label">Patents</div>
                  </div>
                </div>
              </div>

              <div className="modal-social">
                <a href={selectedMember.linkedin} className="social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href={selectedMember.twitter} className="social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TeamPage;
